import React, { useEffect, useMemo, useState } from 'react';
import { Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import axios from 'axios';
import { URL_API } from '../../utils/globalConfig';
import moment from 'moment/moment';
import logoSanef from '../../images/logo.png';
import useAuthz from '../../hooks/useAuthz';
import { FACTURATION_TYPE, PROFIL, REQUEST_PASS_TYPE } from '../../constants/enum';
import { logError } from '../../components/Utils/FormatData';
import FontCalibri from '../../assets/fonts/calibri-regular.ttf';
import FontCalibriBold from '../../assets/fonts/calibri-bold.ttf';
import FontCalibriItalic from '../../assets/fonts/calibri-italic.ttf';
import FontCalibriBoldItalic from '../../assets/fonts/calibri-bold-italic.ttf';
import htmlToDraft from 'html-to-draftjs';

Font.register({
    family: 'Calibri',
    fonts: [
        { src: FontCalibri },
        { src: FontCalibriBold, fontWeight: 'bold' },
        { src: FontCalibriItalic, fontStyle: 'italic' },
        { src: FontCalibriBoldItalic, fontWeight: 'bold', fontStyle: 'italic' },
    ],
});

// Create styles
const styles = StyleSheet.create({
    page: {
        paddingBottom: '45px',
        fontFamily: 'Calibri',
    },
    section: {
        margin: 10,
        padding: 10,
        flexGrow: 0,
    },
    table: {
        display: 'table',
        width: '96%',
        borderStyle: 'solid',
        borderWidth: 1,
        borderRightWidth: 1,
        borderLeftWidth: 0,
        borderBottomWidth: 0,
        margin: '10px',
    },
    table1: {
        display: 'table',
        width: '90%',
        borderStyle: 'solid',
        borderWidth: 0,
        borderRightWidth: 0,
        borderLeftWidth: 0,
        borderBottomWidth: 0,
        margin: '10px',
    },
    tableRow: { margin: 'auto', flexDirection: 'row', borderLeftWidth: 1 },
    tableColumn: { margin: 'auto', flexDirection: 'column', borderLeftWidth: 1, flexWrap: 'wrap' },
    tableCol3: { width: '100%', borderStyle: 'solid', borderWidth: 0, borderLeftWidth: 1, borderBottomWidth: 1 },
    tableCol2: { width: 200, borderStyle: 'solid', borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0 },
    tableCol1: { width: '15.5%', borderStyle: 'solid', borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0 },
    tableCol: { width: 128.35, borderStyle: 'solid', borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0 },
    tableColLeftSpacing: { width: '60%', borderStyle: 'solid', borderWidth: 0, borderLeftWidth: 0, borderTopWidth: 0 },
    tableColRightAlign: { width: '40%', borderStyle: 'solid', borderWidth: 0, borderLeftWidth: 0, borderTopWidth: 0 },
    tableColTotal: { width: '77.5%', borderStyle: 'solid', borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0 },
    tableRowFull: {
        width: '100%',
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderRightWidth: 0,
        borderTopWidth: 0,
        padding: 5,
    },
    tableCell: { margin: '3 auto', fontSize: 8 },
    tableCellNumber: { justifyContent: 'flex-end', marginLeft: 'auto', marginRight: 5, marginTop: 3, fontSize: 8 },
    tableRowGap: {
        marginLeft: 10,
        marginTop: 5,
        marginBottom: -5,
        padding: 5,
        fontSize: 8,
        borderWidth: 1,
        borderBottomWidth: 0,
        width: '96%',
    },
    sectionFoot: {
        margin: 10,
        padding: 10,
        flexGrow: 1,
        maxHeight: '80px',
    },
});

const DevisPDF = (props) => {
    const { type, data, legalNotice, particularNotice } = props;
    const dossier = data.dossier;
    const [authz, user, profil] = useAuthz();

    const chargeMax = useMemo(() => {
        let maxCharge = 0;
        dossier.metadata.caracConvoi.configurations.forEach(config => {
            maxCharge = Math.max(maxCharge, Number(config.chargeMaxiEssieu));
        });
        return maxCharge;
    }, [dossier.metadata.caracConvoi.configurations]);


    const elementsFactures = useMemo(() => {
        const elFact = [data.elements_factures.firstLine];
        if (data.elements_factures.lines) {
            elFact.push(...data.elements_factures.lines);
        }
        if (data.elements_factures.additionalLines) {
            elFact.push(...data.elements_factures.additionalLines);
        }
        return elFact;
    }, [data]);


    const calculateAmountWithTaxes = (onlyTaxesValue = false) => {
        let amount = parseFloat(props.data.montant).toFixed(2);
        let taxes = (amount * 20) / 100;

        if (onlyTaxesValue) {
            return parseFloat(taxes.toString()).toFixed(2);
        } else {
            return parseFloat((Number(taxes) + Number(amount)).toString()).toFixed(2);
        }
    };

    const setCategory = () => {
        let category = dossier.metadata.caracConvoi.category;
        if (category == 3) {
            return '3ème';
        } else if (category == 2) {
            return '2ème';
        } else {
            return '1ère';
        }
    };

    const stylizeChar = (char) => {
        let style = char.getStyle();
        let convertedStyle = [];
        style.map((style) => {
            switch (style) {
                case 'BOLD':
                    convertedStyle = { ...convertedStyle, fontFamily: 'Helvetica-Bold' };
                    break;
                case 'UNDERLINE':
                    convertedStyle = { ...convertedStyle, textDecoration: 'underline' };
                    break;
                case 'ITALIC':
                    convertedStyle = { ...convertedStyle, fontStyle: 'italic' };
                    break;
                default:
                    convertedStyle = { ...convertedStyle, fontFamily: 'Helvetica' };
                    break;
            }
        });
        return convertedStyle;
    };

    const convertComplementDataToHtmlBlock = (blocks) => {
        if (!blocks || blocks.length === 0) return null;

        // Combine the blocks' text into a single structure for better performance
        return blocks.map((block, index) => {
            // Conditional styles
            let textAlign = block.getData().get('text-align') || 'left';
            let justifyContent = 'flex-start';
            if (textAlign === 'center') {
                justifyContent = 'center';
            } else if (textAlign === 'right') {
                justifyContent = 'flex-end';
            }

            let fontSize = '10px';
            if (block.getType() === 'header-one') {
                fontSize = '18px';
            }

            // Combine block text into a single string
            const blockText = block.getText();

            // Render the block with conditional styles
            return (
                <View key={index} style={{
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: justifyContent,  // Dynamic justifyContent
                    lineHeight: 1.5,
                    fontSize: fontSize,              // Dynamic fontSize
                    marginTop: block.getText() === '' || block.getText() === '\n' ? '10px' : '0',
                }}>
                    <Text style={{
                        textAlign: textAlign,        // Dynamic textAlign
                        flex: 1,
                        fontFamily: block.getType() === 'header-one' || block.getType() === 'header-four' ? 'Helvetica-Bold' : 'Helvetica',
                        fontWeight: block.getType() === 'header-one' || block.getType() === 'header-four' ? 'bold' : 'normal',
                        textDecoration: block.getType() === 'header-one' ? 'underline' : 'none',
                    }}>
                        {blockText}
                    </Text>
                </View>
            );
        });
    };


    const convertHtmlToDraftJs = (html) => {
        let { contentBlocks, entityMap } = htmlToDraft(html);
        return { contentBlocks, entityMap };
    };

    const formattedLegalNotice = useMemo(() => {
        if (!legalNotice) return null;
        const render = convertHtmlToDraftJs(legalNotice);
        return convertComplementDataToHtmlBlock(render.contentBlocks);
    }, [legalNotice]);

    const formattedParticularNotice = useMemo(() => {
        if (!particularNotice) return null;
        const render = convertHtmlToDraftJs(particularNotice);
        return convertComplementDataToHtmlBlock(render.contentBlocks);
    }, [particularNotice]);


    const Br = () => '\n';

    let departements = type === FACTURATION_TYPE.DEVIS ? dossier?.metadata.reference.departments : data.elements_factures.status.departement;

    const departmentNumbers = departements.map(department => {
        const match = department.match(/\((\d+)\)/);
        return match ? match[1] : null;
    }).filter(Boolean);

    return (
        <>
        <Page size='A4' style={styles.page}>
            <View fixed={true}>
                <Image
                    src={logoSanef}
                    style={{
                        maxHeight: '80px',
                        maxWidth: '120px',
                        marginLeft: '50px',
                    }}
                    fixed={true}
                />
            </View>
            <View style={{ marginLeft: '20px', marginTop: 15 }}>
                <Text style={{ fontSize: 8, color: 'rgb(33 57 159)' }}>Pôle Transports Exceptionnels</Text>
            </View>

            <View style={styles.table1}> {/* TableHeader */}
                <View style={{ ...styles.tableRow, borderLeftWidth: 0 }} key={'tableRow_1'}>
                    <View style={styles.tableColLeftSpacing}>
                    </View>
                    <View style={styles.tableColRightAlign}>
                        <View style={styles.tableCell}>
                            <View style={{ flexDirection: 'row', width: 150 }}>
                                <View style={{ width: 75 }}>
                                    <Text style={{ fontSize: 8, marginLeft: 'auto' }}>
                                        Date :
                                    </Text>
                                </View>
                                <View style={{ width: 75 }}>
                                    <Text style={{ fontSize: 8, marginLeft: 'auto' }}>
                                        {props.data.elements_factures.creationDate ? moment(props.data.elements_factures.creationDate).format('DD/MM/YYYY') : moment().format('DD/MM/YYYY')}
                                    </Text>
                                </View>
                            </View>
                            <View style={{ flexDirection: 'row', width: 150 }}>
                                <View style={{ width: 75 }}>
                                    <Text style={{ fontSize: 8, marginLeft: 'auto' }}>
                                        Validité du devis :
                                    </Text>
                                </View>
                                <View style={{ width: 75 }}>
                                    <Text style={{ fontSize: 8, marginLeft: 'auto' }}>
                                        {dossier.type_request === REQUEST_PASS_TYPE.SUPERIEUR ? dossier.metadata.datePassage.duration : Math.abs(moment(dossier.metadata.datePassage.startDate).diff(moment(dossier.metadata.datePassage.endDate), 'months')) ?? '0'} mois
                                    </Text>
                                </View>
                            </View>
                        </View>
                    </View>
                </View>
            </View>
            <View style={{ width: '50%', fontSize: 8, marginLeft: 20 }}>
                <View style={{ flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Text>Référence du devis :</Text>
                    <Text style={{ marginLeft: 10 }}>
                        Numéro de dossier : {props.data.dossier.id}
                    </Text>
                    {type === FACTURATION_TYPE.FACTURE &&
                        <Text style={{ marginLeft: 10 }}>
                            Numéro {props.data.elements_factures.devis.length > 1 ? 'des devis' : 'du devis'} : {props.data.elements_factures.devis.join(', ')}
                        </Text>
                    }
                    <Text style={{ marginLeft: 10 }}>
                        Numéro DDT : {props.data.dossier.metadata.reference.numDossier}
                    </Text>
                    <Text style={{ marginLeft: 10 }}>
                        Département(s) concerné(s) : {departmentNumbers.join('- ')}
                    </Text>
                </View>
                <View style={{ flexDirection: 'col' }}>
                    <Text>Mail de contact :</Text>
                    <Text style={{ marginLeft: 10 }}>convois.exceptionnels@sanef.com</Text>
                </View>
            </View>
            <View style={styles.tableRowGap}>
                <Text style={{
                    margin: 'auto',
                    fontSize: 13,
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                }}>{type === FACTURATION_TYPE.DEVIS ? 'Devis' : 'Récapitulatif des éléments à facturer'}</Text>
                <Text style={{ margin: 'auto', fontSize: 8, fontWeight: 'bold' }}>Transports Exceptionnels
                    de {setCategory()} catégorie</Text>
            </View>

            <View style={{ ...styles.tableRowGap, flexDirection: 'row' }}>
                <View style={{ ...styles.tableCol2, margin: -5, padding: 5, borderBottomWidth: 0 }}>
                    <Text style={{ margin: 'auto', fontSize: 13, fontWeight: 'bold' }}>Client :</Text>
                </View>
                <View style={{
                    width: '82.5%',
                    margin: '5px 0px',
                    marginLeft: '15px',
                    fontSize: '8px',
                    flexDirection: 'row',
                    justifyContent: 'space-around',
                    height: 'auto',
                }}>
                    <View>
                        {profil.profil !== PROFIL.ADMINISTRATEUR &&
                            <>
                                <Text style={{ margin: '2px auto', textDecoration: 'underline' }}>Mandataire :</Text>
                                <Text style={{
                                    margin: '2px auto',
                                    textAlign: 'center',
                                }}>{props.data.account.raison_sociale} <Br />{props.data.account.adresse}
                                    <Br /> {props.data.account.code_postal}
                                    <Br /> {props.data.account.commune}<Br /> {props.data.account.pays}</Text>
                            </>
                        }
                        {profil.profil === PROFIL.ADMINISTRATEUR &&
                            <>
                                <Text style={{ margin: '2px auto', textDecoration: 'underline' }}>Mandataire :</Text>
                                <Text style={{
                                    margin: '2px auto',
                                    textAlign: 'center',
                                }}>{dossier.metadata.complementIdentite.mandataire.transporteur}<Br /> {dossier.metadata.complementIdentite.mandataire.adresse}
                                    <Br /> {dossier.metadata.complementIdentite.mandataire.codePostal}<Br /> {dossier.metadata.complementIdentite.mandataire.commune}<Br /> {dossier.metadata.complementIdentite.mandataire.pays}
                                </Text>
                            </>
                        }
                    </View>
                    <View>
                        <Text style={{ margin: '2px auto', textDecoration: 'underline' }}>Pétitionnaire :</Text>
                        <Text style={{
                            margin: '2px auto',
                            textAlign: 'center',
                        }}>{dossier.metadata.complementIdentite.transporteur}<Br /> {dossier.metadata.complementIdentite.adresse}<Br /> {dossier.metadata.complementIdentite.codePostal}<Br /> {dossier.metadata.complementIdentite.commune}<Br /> {dossier.metadata.complementIdentite.pays}
                        </Text>
                    </View>
                </View>
            </View>

            <View style={{ ...styles.tableRowGap, flexDirection: 'row' }}>
                <View style={{
                    ...styles.tableCol2,
                    margin: -5,
                    padding: 5,
                    borderBottomWidth: 0,
                    marginBottom: 5,
                    width: '120px',
                }}>
                    <Text style={{ margin: 'auto', fontSize: 13, fontWeight: 'bold' }}>Objet :</Text>
                </View>
                <View style={{
                    width: '80.5%',
                    margin: '5px 0px',
                    marginLeft: '15px',
                    fontSize: '8px',
                    flexDirection: 'row',
                    height: 'auto',
                }}>
                    <View style={{ marginLeft: -5, fontSize: 9 }}>
                        <View style={{ flexDirection: 'column' }}>
                            <Text style={{ textDecoration: 'underline' }}>Nombre de passage et de convois
                                :</Text>
                            <Text style={{ marginLeft: 5 }}
                                  render={() => ` ${dossier.metadata.datePassage.nbPassageAller} passage(s) Aller de ${dossier.metadata.datePassage.nbVehiculeAller} convoi(s) ${(dossier.metadata.datePassage.nbPassageRetour !== 0 || dossier.metadata.datePassage.nbVehiculeRetour !== 0) ? ' / ' + dossier.metadata.datePassage.nbPassageRetour + ' passage(s) Retour de ' + dossier.metadata.datePassage.nbVehiculeRetour + ' convoi(s)' : ''}`}>
                            </Text>
                        </View>
                        <View style={{ flexDirection: 'column' }}>
                            <Text style={{ textDecoration: 'underline' }}>Description du trajet :</Text>
                            <Text style={{ marginLeft: 5 }}>
                                {
                                    dossier.metadata.trajetDescr.descr.map((section) => {
                                        if (!section.nomSection || section.nomSection === '') {
                                            // Uniquement pour autoroute
                                            return null;
                                        }
                                        return section.nomSection;
                                    }).filter(Boolean).join(' / ')
                                }
                                <span>  -   </span>
                                {
                                    dossier.metadata.trajetDescr.descrRetour.map((section) => {
                                        if (!section.nomSection || section.nomSection === '') {
                                            // Uniquement pour autoroute
                                            return null;
                                        }
                                        return section.nomSection;
                                    }).filter(Boolean).join(' / ')
                                }
                            </Text>
                        </View>
                        <View style={{ flexDirection: 'row', marginTop: 5 }}>
                            <Text style={{ textDecoration: 'underline', minWidth: 40 }}>Validité : </Text>
                            <Text style={{ textAlign: 'left' }}
                                  render={() => (`${dossier.metadata.datePassage.duration} mois à compter de la date de début de validité de l'arrêté`)}></Text>
                        </View>
                        <View style={{ flexDirection: 'row', maxWidth: 220, marginTop: 5 }} wrap={false}>
                            <Text style={{ textDecoration: 'underline', minWidth: 120 }}>Caractéristiques maximales
                                : </Text>
                            <Text>
                                {'Poids : ' + parseFloat((Number(dossier.metadata.caracConvoi.masseTotaleCharge) / 1000).toString()).toFixed(3) + ' T'}
                                {' / L : ' + parseFloat((Number(dossier.metadata.caracConvoi.longueurCharge) / 1000).toString()).toFixed(2) + ' m'}
                                {' / l : ' + parseFloat((Number(dossier.metadata.caracConvoi.largeurCharge) / 1000).toString()).toFixed(2) + ' m'}
                                {' / H : ' + parseFloat((Number(dossier.metadata.caracConvoi.hauteurCharge) / 1000).toString()).toFixed(2) + ' m'}
                                {' / Charge maxi à l\'essieu : ' + (chargeMax === 0 ? '-' : parseFloat((Number(chargeMax) / 1000).toString()).toFixed(2) + ' T')}
                            </Text>
                        </View>
                    </View>
                </View>
            </View>

            <View style={{ ...styles.table, marginTop: -5, marginBottom: 30 }}>
                <View style={styles.tableRow}>
                    <View style={{ ...styles.tableCol2, width: '250px', justifyContent: 'center' }}>
                        <Text style={{
                            ...styles.tableCell,
                            fontSize: 10,
                            fontWeight: 'bold',
                            width: '100%',
                            marginLeft: 4,
                        }}>Désignation</Text>
                    </View>

                    <View style={{ ...styles.tableCol1, width: '95px', justifyContent: 'center' }}>
                        <Text style={{
                            ...styles.tableCell,
                            fontSize: 10,
                            fontWeight: 'bold',
                            width: '100%',
                            marginLeft: 4,
                        }}>Unité</Text>
                    </View>

                    <View style={{ ...styles.tableCol1, maxWidth: '45px', justifyContent: 'center' }}>
                        <Text style={{
                            ...styles.tableCell,
                            fontSize: 10,
                            fontWeight: 'bold',
                            width: '100%',
                            marginLeft: 4,
                        }}>Quantité</Text>
                    </View>

                    <View style={{ ...styles.tableCol1, width: '100px', justifyContent: 'center' }}>
                        <Text style={{
                            ...styles.tableCell,
                            fontSize: 10,
                            fontWeight: 'bold',
                            width: '100%',
                            marginLeft: 4,
                        }}>Prix unitaire HT en €</Text>
                    </View>

                    <View style={{ ...styles.tableCol1, width: '80px', borderRightWidth: 0, justifyContent: 'center' }}>
                        <Text style={{
                            ...styles.tableCell,
                            fontSize: 10,
                            fontWeight: 'bold',
                            width: '100%',
                            marginLeft: 4,
                        }}>Montant HT en €</Text>
                    </View>
                </View>
                {/* TableContent */}
                {elementsFactures.map((el, index) => {
                    if (el && el.tarif !== '' && el.tarif !== 0) {
                        return (<View key={'view-' + index}>
                            <View style={styles.tableRow}>
                                <View style={{ ...styles.tableCol2, width: '250px' }}>
                                    <Text style={{
                                        ...styles.tableCell,
                                        marginLeft: 12,
                                        width: '100%',
                                    }}>{el.libelle} {el.centre ?? ''}</Text>
                                </View>
                                <View style={{ ...styles.tableCol1, width: '95px' }}>
                                    <Text style={styles.tableCell}>{el.unite ?? '-'}</Text>
                                </View>
                                <View style={{ ...styles.tableCol1, maxWidth: '45px' }}>
                                    <Text style={styles.tableCellNumber}>{el.quantite}</Text>
                                </View>

                                <View style={{ ...styles.tableCol1, width: '100px' }}>
                                    <Text style={styles.tableCellNumber}>{parseFloat(el.tarif).toFixed(2) + ' €'}</Text>
                                </View>
                                <View style={{ ...styles.tableCol1, width: '80px', borderRightWidth: 0 }}>
                                    <Text
                                        style={styles.tableCellNumber}>{parseFloat((el.quantite * el.tarif).toString()).toFixed(2) + ' €'}</Text>
                                </View>
                            </View>
                        </View>);
                    }
                })
                }

                <View style={{ ...styles.tableRow }}>
                    <Text style={{ ...styles.tableRowFull, fontSize: '8px' }}>
                        <span style={{ textDecoration: 'underline' }}>Prescriptions particulières</span> :
                        Reconnaissance obligatoire de l'itinéraire par le transporteur avant le convoyage.
                        {dossier.metadata.complInfo.servTeInfo &&
                            <>
                                <Br />
                                <span
                                    style={{ textDecoration: 'underline' }}>Complément d'informations</span> : {dossier.metadata.complInfo.servTeInfo}
                            </>
                        }
                    </Text>
                </View>


                <View style={{ ...styles.tableRow }}>
                    <Text style={{ ...styles.tableRowFull, fontSize: '8px' }}><span
                        style={{ textDecoration: 'underline' }}>Horaires de passage </span> :
                        {dossier.metadata.complInfo.horPassDebut && dossier.metadata.complInfo.horPassFin && (
                            dossier.metadata.complInfo.horPassDebut
                                ? `${moment(dossier.metadata.complInfo.horPassDebut).format('HH:mm')} - ${moment(dossier.metadata.complInfo.horPassFin).format('HH:mm')}`
                                : '-'
                        )}
                    </Text>
                </View>

                <View style={{ ...styles.tableRow }}>
                    <Text style={{ ...styles.tableRowFull, fontSize: '8px', fontWeight: 'bold' }}>Planning prévisionnel
                        à nous fournir dès que possible</Text>
                </View>

                <View style={{ borderBottomWidth: 1, borderLeftWidth: 1, margin: 0 }}>
                    <View style={{ flexDirection: 'column', margin: '5px', fontSize: '8px' }}>
                        <Text><span style={{ textDecoration: 'underline' }}>Prescriptions Service Ouvrage d'Art </span>:
                            <span style={{ marginLeft: '15px' }}> {dossier.metadata.complInfo?.servOaInfo}</span></Text>
                    </View>
                </View>

                <View style={{ borderBottomWidth: 1, borderLeftWidth: 1, margin: 0 }}>
                    <View style={{ flexDirection: 'column', margin: '5px', fontSize: '8px' }}>
                        <Text><span style={{ textDecoration: 'underline' }}>Prescriptions Centre(s) </span>:</Text>
                        {dossier.metadata.complInfo?.refCtr.map((ctr) =>
                            (<>
                                    {ctr.comment && ctr.comment !== '' &&
                                        <View style={{ flexDirection: 'column', margin: '5px', fontSize: '8px' }}>
                                            <Text style={{ textDecoration: 'underline' }}>{ctr.name} :</Text>
                                            <Text
                                                style={{ marginLeft: '15px' }}>{ctr.comment !== '' ? ctr.comment : '-'}</Text>
                                        </View>
                                    }
                                </>
                            ))
                        }
                    </View>
                </View>

                <View style={{ ...styles.tableRow }}>
                    <Text style={{ ...styles.tableRowFull, fontSize: '8px' }}>Prévenir au minimum 4 jours ouvrés avant
                        la date de chaque intervention.</Text>
                </View>

                <View style={{ ...styles.tableRow }}>
                    <Text style={{ ...styles.tableRowFull, fontSize: '8px' }}>Délai de prévenance en cas d'annulation
                        du passage 48h ouvrées avant réalisation avec confirmation par mail.</Text>
                </View>

                <View style={{ ...styles.tableRow }}>
                    <Text style={{ ...styles.tableRowFull, fontSize: '8px' }}>Sanef se réserve le droit d'annuler au
                        dernier moment le passage du convoi en raison d'accidents ou d'événement exceptionnels.</Text>
                </View>

                <View style={{ ...styles.tableRow }}>
                    <Text style={{ ...styles.tableRowFull, fontSize: '8px', fontWeight: 'bold' }}>(Acquittement des
                        frais de péage non inclus)</Text>
                </View>

                <View style={{ ...styles.tableRow, borderTopWidth: 0 }}>
                    <View style={styles.tableColTotal}>
                        <Text
                            style={{ fontSize: 8, marginTop: 3, marginBottom: 3, marginRight: 'auto', marginLeft: 5 }}>Montant
                            Total HT</Text>
                    </View>
                    <View style={{ ...styles.tableCol, borderRightWidth: 0 }}>
                        <Text style={styles.tableCellNumber}>{parseFloat(props.data.montant).toFixed(2) + ' €'}</Text>
                    </View>
                </View>
                <View style={{ ...styles.tableRow, borderTopWidth: 0 }}>
                    <View style={styles.tableColTotal}>
                        <Text
                            style={{ fontSize: 8, marginTop: 3, marginBottom: 3, marginRight: 'auto', marginLeft: 5 }}>TVA
                            20
                            %</Text>
                    </View>
                    <View style={{ ...styles.tableCol, borderRightWidth: 0 }}>
                        <Text style={styles.tableCellNumber}>{calculateAmountWithTaxes(true) + ' €'}</Text>
                    </View>
                </View>
                <View style={{ ...styles.tableRow, borderTopWidth: 0 }}>
                    <View style={styles.tableColTotal}>
                        <Text
                            style={{ fontSize: 8, marginTop: 3, marginBottom: 3, marginRight: 'auto', marginLeft: 5 }}>Montant
                            TTC en euros</Text>
                    </View>
                    <View style={{ ...styles.tableCol, borderRightWidth: 0 }}>
                        <Text style={styles.tableCellNumber}>{calculateAmountWithTaxes() + ' €'}</Text>
                    </View>
                </View>
                <View style={{ ...styles.tableRow, borderTopWidth: 0, borderRightWidth: 0, borderLeftWidth: 0 }}>
                    <View style={{ ...styles.tableColLeftSpacing, borderTopWidth: 0, borderLeftWidth: 0 }}>
                    </View>
                    <View style={{
                        ...styles.tableColRightAlign,
                        borderTopWidth: 0,
                        marginLeft: 'auto',
                        borderBottomWidth: 1,
                        borderLeftWidth: 1,
                    }}>
                        <View>
                            <Text style={{
                                width: '100%',
                                fontSize: 8,
                                marginTop: 3,
                                marginBottom: 3,
                                marginLeft: 'auto',
                                marginRight: 5,
                                paddingLeft: '5px',
                            }}>
                                Entité à facturer: {dossier.metadata.complementIdentite.tiers?.transporteur ?? ''}
                            </Text>
                        </View>
                    </View>
                </View>
            </View>

            <View style={{
                marginRight: 'auto',
                bottom: 0,
                left: 0,
                marginLeft: 30,
                display: 'flex',
                flexDirection: 'row',
            }}>
                <Image src={props.signature} style={{ maxHeight: '40px', maxWidth: '120px' }} />
                <View style={{
                    marginLeft: 10,
                    borderRightWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'start',
                    backgroundColor: 'red',
                }}>
                    <Text
                        style={{ margin: 'auto', fontSize: 8, fontWeight: 'bold', borderRightWidth: 0, width: '100%' }}>Date
                        : {props.data.elements_factures.returnDate ? moment(props.data.elements_factures.returnDate).format('DD/MM/YYYY') : '-'}</Text>
                    <Text
                        style={{ margin: 'auto', fontSize: 8, fontWeight: 'bold', borderRightWidth: 0, width: '100%' }}>Signature
                        du
                        client précédée de la mention "Lu et accepté" :</Text>
                    <Text
                        style={{ margin: 'auto', fontSize: 8, fontWeight: 'bold', borderRightWidth: 0, width: '100%' }}>N°
                        de TVA
                        intracommunautaire : {dossier.metadata.complementIdentite.tiers?.numTva ?? "-"}</Text>
                    <Text
                        style={{ margin: 'auto', fontSize: 8, fontWeight: 'bold', borderRightWidth: 0, width: '100%' }}>Mail
                        signataire
                        du devis : {dossier.metadata.complementIdentite.tiers?.email}</Text>
                </View>
            </View>
            {/*<View>*/}
            {/*  <View style={{marginTop: 'auto'}}>*/}
            {/*    <Text style={{ fontSize: '9px', margin: '0 auto' }}>Site de l'Ecopôle - Route de Thillois - ORMES - CS 30011 - 51886 REIMS Cedex</Text>*/}
            {/*    <Text style={{ fontSize: '9px', margin: '0 auto' }}>S.A. au capital de 53 090 461 euros - RCS Nanterre B 632 050 019 060 019</Text>*/}
            {/*  </View>*/}
            {/*</View>*/}

            <View fixed={true}
                  style={{ position: 'absolute', fontSize: 5, bottom: 15, left: 0, right: 0, textAlign: 'center' }}>
                <Text>
                    Site de l'Ecopôle - Route de Thillois - Ormes
                    <Br />
                    CS 30011 51886 REIMS Cedex
                    <Br />
                    S.A. au capital de 53 090 461,67 euros - RCS Nanterre B632 050 019
                </Text>
            </View>
        </Page>
        {(formattedLegalNotice || formattedParticularNotice) && (
            <Page size='A4' style={styles.page}>
                <View fixed={true}>
                    <Image
                        src={logoSanef}
                        style={{
                            maxHeight: '80px',
                            maxWidth: '120px',
                            marginLeft: '50px',
                        }}
                        fixed={true}
                    />
                </View>
                <View style={{
                    display: 'flex',
                    flexDirection: 'column',
                    fontSize: '10px',
                    margin: '20px 40px',
                    justifyContent: 'center',
                }}>
                    {formattedLegalNotice && <View>{formattedLegalNotice}</View>}
                    {formattedParticularNotice && <View style={{ marginTop: '10px' }}>{formattedParticularNotice}</View>}
                </View>
                <View fixed={true}
                      style={{ position: 'absolute', fontSize: 5, bottom: 15, left: 0, right: 0, textAlign: 'center' }}>
                    <Text>
                        Site de l'Ecopôle - Route de Thillois - Ormes
                        <Br />
                        CS 30011 51886 REIMS Cedex
                        <Br />
                        S.A. au capital de 53 090 461,67 euros - RCS Nanterre B632 050 019
                    </Text>
                </View>
            </Page>
        )}
        </>
    );
};

export default React.memo(DevisPDF);
